import { renderToStaticMarkup } from "react-dom/server";

/**
 * Prints a React element as a standalone document inside a hidden iframe, so the
 * dashboard stylesheets, sidebar and interactive controls never reach the paper.
 */

const FRAME_ID = "tdcp-print-frame";
const IMAGE_TIMEOUT = 6000;

const BASE_STYLES = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #212529;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  table { border-collapse: collapse; width: 100%; }
  tr, img { page-break-inside: avoid; }
  @page { size: A4; margin: 12mm; }
`;

const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char])
  );

const waitForImages = (doc) => {
  const pending = Array.from(doc.images || []).filter((image) => !image.complete);
  if (!pending.length) return Promise.resolve();

  const loaded = Promise.all(
    pending.map(
      (image) =>
        new Promise((resolve) => {
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", resolve, { once: true });
        })
    )
  );

  // A slow or broken logo must never block the print dialog.
  return Promise.race([
    loaded,
    new Promise((resolve) => setTimeout(resolve, IMAGE_TIMEOUT)),
  ]);
};

export const printReactElement = async (element, { title = "Document" } = {}) => {
  document.getElementById(FRAME_ID)?.remove();

  const frame = document.createElement("iframe");
  frame.id = FRAME_ID;
  frame.setAttribute("aria-hidden", "true");
  // Parked off-screen at page size: hidden or zero-sized frames do not print.
  frame.style.cssText =
    "position:fixed;top:0;left:-10000px;width:210mm;height:297mm;border:0;";
  document.body.appendChild(frame);

  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(
      title
    )}</title><style>${BASE_STYLES}</style></head><body>${renderToStaticMarkup(
      element
    )}</body></html>`
  );
  doc.close();

  await waitForImages(doc);
  if (!frame.contentWindow) return;

  const cleanUp = () => document.getElementById(FRAME_ID)?.remove();
  frame.contentWindow.addEventListener("afterprint", cleanUp, { once: true });

  frame.contentWindow.focus();
  frame.contentWindow.print();

  // Browsers that never fire afterprint still need the frame cleared.
  setTimeout(cleanUp, 60000);
};

export default printReactElement;
